/**
 * Validation primitives, defined once and used by both sides.
 *
 * The server validates every request against these, and the client validates the
 * same forms before sending, so the two cannot disagree about what is acceptable.
 * The old codebase had no runtime validation anywhere: `patchSingleton` accepted
 * arbitrary fields and `$set` them, while the allowlist that should have filtered
 * them sat three lines away, used for reads only.
 *
 * Every object schema here is strict. An unknown field is a 400, not something
 * quietly written to the database.
 *
 * Audit references prevented here:
 *   server.js #20  (patchSingleton accepted arbitrary fields)
 *   server.js #19  (a body field could reassign record ownership)
 *   state.js #16   (no runtime schema for a 90-field document)
 */

import { z } from 'zod';

/**
 * Where something happened: the lab, the store, the clinic.
 *
 * Shared so blood work, groceries and calendar events describe a place the same way and
 * a single picker can fill any of them. Every field is optional: a place is always an
 * enrichment, never a reason a record cannot be saved.
 */
export const placeFields = {
  venue: z.string().trim().max(160).nullable().optional(),
  address: z.string().trim().max(300).nullable().optional(),
  mapsUrl: z.string().url().max(1000).nullable().optional(),
};

import { DATE_KEY_PATTERN, isCalendarDateKey } from '../time/date-key.js';
import { IDEMPOTENCY_KEY_PATTERN, RECORD_ID_PATTERN } from '../ids/identifiers.js';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '../http/envelope.js';

/**
 * `YYYY-MM-DD` in the application timezone.
 *
 * The regex alone accepts "2026-02-31" - the right shape, not a real day - and every
 * date-keyed collection in this app trusts that a stored `dateKey` is a real calendar
 * date. `isCalendarDateKey` is the same round-trip-through-`Date.UTC` check already
 * relied on client-side when a date typed into a form might not exist; this is that same
 * check enforced at the boundary every request actually crosses.
 */
export const dateKeySchema = z
  .string()
  .regex(DATE_KEY_PATTERN, 'Expected a YYYY-MM-DD date')
  .refine(isCalendarDateKey, 'Expected a real calendar date');

/** A server-minted record id, as the client sees it. */
export const recordIdSchema = z
  .string()
  .regex(RECORD_ID_PATTERN, 'Expected a 24 character record id');

/** A client-minted idempotency key. */
export const idempotencyKeySchema = z
  .string()
  .regex(IDEMPOTENCY_KEY_PATTERN, 'Expected a UUID idempotency key');

/**
 * The version a client read before writing.
 *
 * Required on every update. The old server treated an absent version as consent to
 * a blind upsert, with a comment saying "same behavior as before this existed", so
 * any caller that forgot the field silently bypassed the whole protection.
 */
export const versionSchema = z
  .number()
  .int('Version must be a whole number')
  .nonnegative('Version cannot be negative');

/** A wall-clock time of day, zero padded so string ordering matches real ordering. */
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a zero-padded HH:MM time');

/** An email address, normalised to lowercase so identity cannot fork by casing. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Expected a valid email address')
  .max(254, 'That email address is too long');

/**
 * Cursor pagination query, applied to every list that can grow without bound.
 *
 * Cursor rather than offset: a health record grows while it is being read, and an
 * offset page shifts under the reader as rows arrive.
 */
export const paginationQuerySchema = z.strictObject({
  cursor: z.string().max(200).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE)
    .optional(),
});

/**
 * Build the standard params schema for a per-day resource.
 *
 * @returns {import('zod').ZodTypeAny}
 */
export function dayParamsSchema() {
  return z.strictObject({ dateKey: dateKeySchema });
}

/**
 * Flatten a Zod failure into safe, per-field detail for the client.
 *
 * Field paths and Zod's own messages are safe to show. Nothing internal leaks,
 * because nothing internal is in a Zod issue.
 *
 * @param {import('zod').ZodError} error
 * @returns {{fields: Record<string, string>}}
 */
export function toValidationDetails(error) {
  const fields = {};
  for (const issue of error.issues) {
    const path = issue.path.length > 0 ? issue.path.join('.') : '_';
    if (!fields[path]) fields[path] = issue.message;
  }
  return { fields };
}
